import React, { useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Badge, Checkbox, Select, Spinner, Table, TextInput } from 'flowbite-react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Title from '../../../components/Title';
import SubTitle from '../../../components/SubTitle';
import Button from '../../../components/Button';
import Notification from '../../../components/Notification';
import ToastNotification from '../../../components/ToastNotification';
import DefaultModal from '../../../components/modals/DefaultModal';
import { ToastType } from '../../../typesData/ToastNotificationTypes';
import { getApiErrorMessage } from '../helpers';
import { formatMessage, printShopStrings } from '../strings';
import { AssetKind } from '../types';
import {
  createPricingConfig,
  deactivatePricingConfig,
  fetchPricingConfigs,
  updatePricingConfig,
} from './api';
import { toPricingConfigPayload, validatePricingConfigForm } from './helpers';
import { pricingConfigStrings } from './strings';
import {
  PricingConfig,
  PricingConfigFormErrors,
  PricingConfigFormValues,
} from './types';

// id forces <Notification> to remount per banner — the shared component
// hides itself via a mount-once timeout and never resets, so reusing the
// same element renders nothing after the first 5-second window.
type Banner = { id: number; type: ToastType; message: string };
let bannerSequence = 0;
const nextBanner = (type: ToastType, message: string): Banner => ({
  id: (bannerSequence += 1),
  type,
  message,
});

const ASSET_KINDS: AssetKind[] = ['book', 'tb_export'];

const QUERY_KEY = 'print-shop-pricing-configs';

const emptyForm = (): PricingConfigFormValues => ({
  assetKind: 'book',
  creditsPerRecipient: '',
  minQuantity: '',
  isActive: true,
});

/**
 * Mail-on-Demand pricing admin: one credits-per-recipient rate and minimum
 * quantity per asset kind. The backend keeps a single active config per kind
 * — creating or activating a config auto-deactivates the previous one.
 */
export default function PricingConfigPage() {
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<Banner | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PricingConfig | null>(null);
  const [formValues, setFormValues] = useState<PricingConfigFormValues>(
    emptyForm(),
  );
  const [formErrors, setFormErrors] = useState<PricingConfigFormErrors>({});
  const [deactivating, setDeactivating] = useState<PricingConfig | null>(null);

  const {
    data: configs,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<PricingConfig[]>(QUERY_KEY, fetchPricingConfigs, {
    refetchOnWindowFocus: false,
  });

  const kindLabel = (config: PricingConfig) =>
    printShopStrings.assetKindLabels[config.assetKind];

  // The create-new picker only ever offers ASSET_KINDS (book/tb_export), but
  // an existing row being edited may carry a kind outside that curated list
  // (e.g. the book_cover_letter_bundle ADR-CL-004 key) — append it here only
  // so the disabled edit-mode Select always has a matching option, without
  // widening what a NEW config can be created as.
  const selectableAssetKinds: AssetKind[] =
    editing && !ASSET_KINDS.includes(editing.assetKind)
      ? [...ASSET_KINDS, editing.assetKind]
      : ASSET_KINDS;

  const afterMutation = (message: string) => {
    queryClient.invalidateQueries(QUERY_KEY);
    setBanner(nextBanner('SUCCESS', message));
    ToastNotification({ type: 'success', message });
  };

  const onMutationError = (error: unknown, fallback: string) => {
    const message = getApiErrorMessage(error, fallback);
    setBanner(nextBanner('FAILURE', message));
    ToastNotification({ type: 'failure', message });
  };

  const saveMutation = useMutation(
    () => {
      const payload = toPricingConfigPayload(formValues);
      if (editing) {
        return updatePricingConfig(editing._id, {
          creditsPerRecipient: payload.creditsPerRecipient,
          minQuantity: payload.minQuantity,
          isActive: payload.isActive,
        });
      }
      return createPricingConfig(payload);
    },
    {
      onSuccess: (saved) => {
        afterMutation(
          formatMessage(
            editing
              ? pricingConfigStrings.messages.updateSuccess
              : pricingConfigStrings.messages.createSuccess,
            { assetKind: printShopStrings.assetKindLabels[saved.assetKind] },
          ),
        );
        closeForm();
      },
      onError: (error) =>
        onMutationError(error, pricingConfigStrings.messages.saveError),
    },
  );

  const deactivateMutation = useMutation(
    (config: PricingConfig) => deactivatePricingConfig(config._id),
    {
      onSuccess: (_data, config) => {
        afterMutation(
          formatMessage(pricingConfigStrings.messages.deactivateSuccess, {
            assetKind: kindLabel(config),
          }),
        );
      },
      onError: (error) =>
        onMutationError(error, pricingConfigStrings.messages.deactivateError),
      onSettled: () => setDeactivating(null),
    },
  );

  const openCreate = () => {
    setEditing(null);
    setFormValues(emptyForm());
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (config: PricingConfig) => {
    setEditing(config);
    setFormValues({
      assetKind: config.assetKind,
      creditsPerRecipient: String(config.creditsPerRecipient),
      minQuantity: String(config.minQuantity),
      isActive: config.isActive,
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setFormErrors({});
  };

  const submitForm = () => {
    const errors = validatePricingConfigForm(formValues);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0 || saveMutation.isLoading) {
      return;
    }
    saveMutation.mutate();
  };

  return (
    <div className="pt-8 min-h-screen w-full">
      <ToastContainer />
      <div className="mb-8 flex items-center justify-between">
        <Title>{pricingConfigStrings.pageTitle}</Title>
        <div className="flex gap-2">
          <Button
            variant="OUTLINED"
            isProcessing={isFetching && !isLoading}
            onClick={() => refetch()}
          >
            {pricingConfigStrings.refreshButton}
          </Button>
          <Button variant="PRIMARY" onClick={openCreate}>
            {pricingConfigStrings.createButton}
          </Button>
        </div>
      </div>

      {banner && (
        <Notification key={banner.id} type={banner.type} message={banner.message} />
      )}

      <div className="px-4 py-4 shadow-md bg-white rounded-lg">
        <div className="px-4 py-2">
          <SubTitle>{pricingConfigStrings.sectionTitle}</SubTitle>
          <p className="text-neutral-500 text-xs mt-1">
            {pricingConfigStrings.sectionHint}
          </p>
        </div>

        {isError && (
          <div className="px-4">
            <Notification
              type="FAILURE"
              message={pricingConfigStrings.loadError}
            />
          </div>
        )}

        {isLoading ? (
          <div className="px-4 py-10 flex justify-center">
            <Spinner size="lg" aria-label={pricingConfigStrings.pageTitle} />
          </div>
        ) : (
          <div className="px-4 py-2 overflow-x-auto">
            <Table data-testid="pricing-config-table">
              <Table.Head className="whitespace-nowrap text-gray-900 capitalize border-b border-t">
                <Table.HeadCell className="px-4 py-3">
                  {pricingConfigStrings.columns.assetKind}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {pricingConfigStrings.columns.creditsPerRecipient}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {pricingConfigStrings.columns.minQuantity}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {pricingConfigStrings.columns.status}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {pricingConfigStrings.columns.actions}
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y whitespace-nowrap">
                {(configs ?? []).map((config) => (
                  <Table.Row
                    key={config._id}
                    data-testid={`pricing-config-row-${config._id}`}
                    className="border-b"
                  >
                    <Table.Cell className="px-4 py-3 font-semibold text-neutral-800">
                      {kindLabel(config)}
                    </Table.Cell>
                    <Table.Cell className="px-4 py-3">
                      {config.creditsPerRecipient}
                    </Table.Cell>
                    <Table.Cell className="px-4 py-3">
                      {config.minQuantity}
                    </Table.Cell>
                    <Table.Cell className="px-4 py-3">
                      <Badge
                        color={config.isActive ? 'success' : 'gray'}
                        className="w-fit"
                      >
                        {config.isActive
                          ? pricingConfigStrings.activeBadge
                          : pricingConfigStrings.inactiveBadge}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          variant="OUTLINED"
                          size="xs"
                          onClick={() => openEdit(config)}
                        >
                          {pricingConfigStrings.editButton}
                        </Button>
                        {config.isActive && (
                          <Button
                            variant="DANGER"
                            size="xs"
                            onClick={() => setDeactivating(config)}
                          >
                            {pricingConfigStrings.deactivateButton}
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {!isError && (configs ?? []).length === 0 && (
              <p className="text-neutral-500 text-sm px-4 py-6">
                {pricingConfigStrings.empty}
              </p>
            )}
          </div>
        )}
      </div>

      <DefaultModal
        title={
          editing
            ? formatMessage(pricingConfigStrings.form.editTitle, {
                assetKind: printShopStrings.assetKindLabels[editing.assetKind],
              })
            : pricingConfigStrings.form.createTitle
        }
        showModal={formOpen}
        setShowModal={(show) => {
          if (!show) closeForm();
        }}
        size="md"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="pricing-asset-kind"
              className="text-sm text-neutral-800 font-semibold"
            >
              {pricingConfigStrings.form.assetKindLabel}
            </label>
            <Select
              id="pricing-asset-kind"
              data-testid="pricing-asset-kind"
              value={formValues.assetKind}
              disabled={editing !== null}
              onChange={(event) =>
                setFormValues((values) => ({
                  ...values,
                  assetKind: event.target.value as AssetKind,
                }))
              }
            >
              {selectableAssetKinds.map((kind) => (
                <option key={kind} value={kind}>
                  {printShopStrings.assetKindLabels[kind]}
                </option>
              ))}
            </Select>
            {editing && (
              <p className="text-neutral-500 text-xs">
                {pricingConfigStrings.form.assetKindLockedHint}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="pricing-credits"
              className="text-sm text-neutral-800 font-semibold"
            >
              {pricingConfigStrings.form.creditsPerRecipientLabel}
            </label>
            <TextInput
              id="pricing-credits"
              data-testid="pricing-credits"
              type="number"
              min="0"
              step="any"
              value={formValues.creditsPerRecipient}
              onChange={(event) =>
                setFormValues((values) => ({
                  ...values,
                  creditsPerRecipient: event.target.value,
                }))
              }
            />
            {formErrors.creditsPerRecipient && (
              <p className="text-red-600 text-xs" role="alert">
                {formErrors.creditsPerRecipient}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="pricing-min-quantity"
              className="text-sm text-neutral-800 font-semibold"
            >
              {pricingConfigStrings.form.minQuantityLabel}
            </label>
            <TextInput
              id="pricing-min-quantity"
              data-testid="pricing-min-quantity"
              type="number"
              min="1"
              step="1"
              value={formValues.minQuantity}
              onChange={(event) =>
                setFormValues((values) => ({
                  ...values,
                  minQuantity: event.target.value,
                }))
              }
            />
            {formErrors.minQuantity && (
              <p className="text-red-600 text-xs" role="alert">
                {formErrors.minQuantity}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id="pricing-is-active"
                data-testid="pricing-is-active"
                checked={formValues.isActive}
                onChange={(event) =>
                  setFormValues((values) => ({
                    ...values,
                    isActive: event.target.checked,
                  }))
                }
              />
              <label
                htmlFor="pricing-is-active"
                className="text-sm text-neutral-800 font-semibold"
              >
                {pricingConfigStrings.form.isActiveLabel}
              </label>
            </div>
            <p className="text-neutral-500 text-xs">
              {pricingConfigStrings.form.isActiveHint}
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="OUTLINED" onClick={closeForm}>
              {pricingConfigStrings.form.cancelButton}
            </Button>
            <Button
              variant="PRIMARY"
              isProcessing={saveMutation.isLoading}
              disabled={saveMutation.isLoading}
              onClick={submitForm}
            >
              {pricingConfigStrings.form.saveButton}
            </Button>
          </div>
        </div>
      </DefaultModal>

      <DefaultModal
        title={pricingConfigStrings.deactivate.title}
        showModal={deactivating !== null}
        setShowModal={(show) => {
          if (!show) setDeactivating(null);
        }}
        size="md"
      >
        {deactivating && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-neutral-800">
              {formatMessage(pricingConfigStrings.deactivate.message, {
                assetKind: kindLabel(deactivating),
                credits: String(deactivating.creditsPerRecipient),
              })}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="OUTLINED" onClick={() => setDeactivating(null)}>
                {pricingConfigStrings.deactivate.cancelButton}
              </Button>
              <Button
                variant="DANGER"
                isProcessing={deactivateMutation.isLoading}
                disabled={deactivateMutation.isLoading}
                onClick={() => deactivateMutation.mutate(deactivating)}
              >
                {pricingConfigStrings.deactivate.confirmButton}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>
    </div>
  );
}
